/**
 * AIDiagnosticsPanel — Diagnóstico IA de Super Admin Connect
 * Escanea toda la infraestructura, detecta errores y genera
 * instrucciones listas para pegar en Replit Agent.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bot, RefreshCw, Copy, CheckCircle2, XCircle, AlertTriangle,
  ChevronDown, ChevronUp, Zap, Shield, Users, Wallet, Globe,
  Webhook, Server, Key, Link2, Activity, ClipboardCopy,
  TrendingUp, Play, BarChart3, AlertCircle, ExternalLink,
  Database
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEV_CFG = {
  critical: { color: 'text-red-400',    bg: 'bg-red-500/10 border-red-500/30',    icon: XCircle,       label: 'CRÍTICO' },
  high:     { color: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/30', icon: AlertCircle, label: 'ALTO'    },
  medium:   { color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/30', icon: AlertTriangle, label: 'MEDIO' },
  ok:       { color: 'text-green-400',  bg: 'bg-green-500/10 border-green-500/30',  icon: CheckCircle2,  label: 'OK'     },
};

const MODULE_ICONS = {
  connectivity:   Globe,
  api_auth:       Key,
  endpoint_scan:  Server,
  oauth:          Shield,
  user_sync:      Users,
  user_propagation: Users,
  wallet_sync:    Wallet,
  wallet_balance: Wallet,
  webhooks:       Webhook,
  okbot_agent:    Bot,
  reverse_link:   Link2,
  verification:   CheckCircle2,
  cross_platform_users: Database,
  webhook_receiver: Webhook,
  user_propagation_route: Database,
};

function ScoreRing({ score = 0, size = 72 }) {
  const radius = 28, cx = size / 2, cy = size / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? '#22C55E' : score >= 60 ? '#3B82F6' : score >= 40 ? '#F59E0B' : '#EF4444';
  return (
    <svg width={size} height={size} className="flex-shrink-0">
      <circle cx={cx} cy={cy} r={radius} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="5" />
      <circle cx={cx} cy={cy} r={radius} fill="none" stroke={color} strokeWidth="5"
        strokeDasharray={circumference} strokeDashoffset={offset}
        strokeLinecap="round" transform={`rotate(-90 ${cx} ${cy})`}
        style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
      <text x={cx} y={cy + 5} textAnchor="middle" fill="white" fontSize="13" fontWeight="bold">{score}%</text>
    </svg>
  );
}

function StatusDot({ status }) {
  const colors = { ok: 'bg-green-400', warning: 'bg-yellow-400', error: 'bg-red-400' };
  return <span className={`w-2 h-2 rounded-full flex-shrink-0 ${colors[status] || 'bg-gray-500'}`} />;
}

export default function AIDiagnosticsPanel() {
  const [loading, setLoading]           = useState(false);
  const [propagating, setPropagating]   = useState(false);
  const [report, setReport]             = useState(null);
  const [propStatus, setPropStatus]     = useState(null);
  const [copied, setCopied]             = useState(false);
  const [expandedPlatform, setExpandedPlatform] = useState(null);
  const [expandedIssues, setExpandedIssues]     = useState(true);
  const [activeView, setActiveView]     = useState('report'); // 'report' | 'instructions'

  const load = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/admin/connect/diagnostics-report`),
        axios.get(`${API}/admin/connect/propagation-status`),
      ]);
      if (r1.data && r1.data.ran_at) setReport(r1.data);
      if (r2.data && r2.data.ran_at) setPropStatus(r2.data);
    } catch (e) {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRunDiagnostics = async () => {
    setLoading(true);
    try {
      toast.info('Analizando infraestructura... esto puede tomar 30-60 segundos');
      const r = await axios.post(`${API}/admin/connect/run-diagnostics`);
      setReport(r.data);
      setActiveView('report');
      const score = r.data.overall_score;
      toast[score >= 70 ? 'success' : 'warning'](
        `Diagnóstico completado: ${score}% — ${r.data.total_issues} problemas detectados`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al ejecutar diagnóstico');
    } finally {
      setLoading(false);
    }
  };

  const handlePropagate = async () => {
    setPropagating(true);
    try {
      toast.info('Propagando usuarios entre todas las plataformas...');
      const r = await axios.post(`${API}/admin/connect/propagate-all-users`);
      setPropStatus(r.data);
      toast.success(
        `Propagación completada: ${r.data.total_registered} usuarios registrados en OkBot, ` +
        `${r.data.total_propagated} propagaciones a otras plataformas`
      );
      load();
    } catch (e) {
      toast.error('Error en propagación de usuarios');
    } finally {
      setPropagating(false);
    }
  };

  const copyInstructions = () => {
    if (!report?.llm_instructions) return;
    navigator.clipboard.writeText(report.llm_instructions);
    setCopied(true);
    toast.success('✓ Instrucciones copiadas al portapapeles');
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <div className="space-y-4">
      {/* Header actions */}
      <div className="glass-panel p-4">
        <div className="flex flex-wrap items-start gap-4">
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-semibold text-base flex items-center gap-2">
              <Bot className="w-5 h-5 text-red-400" />
              Diagnóstico IA de Infraestructura
            </h3>
            <p className="text-gray-500 text-xs mt-1">
              El agente IA escanea toda la infraestructura del Super Admin Connect, detecta errores y
              genera instrucciones precisas listas para pegar en Replit Agent y solucionar todo automáticamente.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={handleRunDiagnostics} disabled={loading}
              className="btn-primary flex items-center gap-2 text-sm">
              {loading
                ? <RefreshCw className="w-4 h-4 animate-spin" />
                : <Zap className="w-4 h-4" />}
              {loading ? 'Analizando infraestructura...' : 'Ejecutar Diagnóstico IA'}
            </button>
            <button onClick={handlePropagate} disabled={propagating}
              className="btn-secondary flex items-center gap-2 text-sm">
              {propagating
                ? <RefreshCw className="w-4 h-4 animate-spin" />
                : <Users className="w-4 h-4" />}
              {propagating ? 'Propagando...' : 'Propagar Usuarios Universalmente'}
            </button>
          </div>
        </div>

        {/* Propagation last result */}
        {propStatus && (
          <div className="mt-3 pt-3 border-t border-white/5 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            {[
              { label: 'Plataformas escaneadas', val: propStatus.platforms_scanned, color: 'text-blue-400' },
              { label: 'Registrados en OkBot',   val: propStatus.total_registered,  color: 'text-green-400' },
              { label: 'Propagados a otras',      val: propStatus.total_propagated,  color: 'text-purple-400' },
              { label: 'Última ejecución',        val: propStatus.ran_at?.slice(0,16) || '—', color: 'text-gray-400', isText: true },
            ].map(({ label, val, color, isText }) => (
              <div key={label} className="bg-white/5 rounded-lg p-2">
                <p className={`font-bold text-sm ${color}`}>{isText ? val : val ?? 0}</p>
                <p className="text-gray-600 text-[10px]">{label}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Loading state */}
      {loading && (
        <div className="glass-panel p-8 text-center">
          <RefreshCw className="w-10 h-10 text-red-400 animate-spin mx-auto mb-3" />
          <p className="text-gray-300 font-medium">Analizando infraestructura completa...</p>
          <p className="text-gray-600 text-sm mt-1">
            Verificando conectividad, OAuth, usuarios, wallets, webhooks y agentes IA en todas las plataformas.
          </p>
          <div className="flex justify-center gap-4 mt-4 text-xs text-gray-600">
            {['Conectividad', 'API Auth', 'OAuth SSO', 'Usuarios', 'OkWallet', 'Webhooks', 'OkBot Agent'].map(s => (
              <span key={s} className="flex items-center gap-1">
                <RefreshCw className="w-3 h-3 animate-spin" />{s}
              </span>
            ))}
          </div>
        </div>
      )}

      {report && !loading && (
        <>
          {/* Overall Score + View Toggle */}
          <div className="glass-panel p-5">
            <div className="flex items-center gap-5 mb-4">
              <ScoreRing score={report.overall_score} size={80} />
              <div className="flex-1">
                <h3 className="text-white font-bold text-xl">
                  {report.overall_score >= 80 ? '✅' : report.overall_score >= 50 ? '⚠️' : '🚨'}{' '}
                  Infraestructura {report.overall_score}% operativa
                </h3>
                <p className="text-gray-400 text-sm mt-1">
                  {report.platforms_scanned} plataformas analizadas · {report.total_issues} problemas detectados
                </p>
                <div className="flex gap-4 mt-2 text-xs">
                  <span className="text-red-400 flex items-center gap-1">
                    <XCircle className="w-3.5 h-3.5" />{report.critical_issues} críticos
                  </span>
                  <span className="text-orange-400 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />{report.high_issues} altos
                  </span>
                  <span className="text-yellow-400 flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" />{report.medium_issues} medios
                  </span>
                  <span className="text-gray-500 text-[10px] ml-auto">
                    Analizado: {report.ran_at?.slice(0, 16)}
                  </span>
                </div>
              </div>
            </div>

            {/* View toggle */}
            <div className="flex gap-2">
              <button onClick={() => setActiveView('report')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors ${
                  activeView === 'report' ? 'bg-red-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}>
                <BarChart3 className="w-3.5 h-3.5" />Reporte de Problemas
              </button>
              <button onClick={() => setActiveView('instructions')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors ${
                  activeView === 'instructions' ? 'bg-red-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}>
                <Bot className="w-3.5 h-3.5" />
                Instrucciones para Replit Agent
                {report.critical_issues > 0 && (
                  <span className="w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center">
                    {report.critical_issues}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* ── REPORT VIEW ── */}
          {activeView === 'report' && (
            <div className="space-y-4">
              {/* Per-platform status */}
              {(report.platform_reports || []).map((pr, i) => (
                <div key={pr.platform_id} className="glass-panel overflow-hidden">
                  <button
                    onClick={() => setExpandedPlatform(expandedPlatform === i ? null : i)}
                    className="w-full flex items-center gap-3 p-4 hover:bg-white/3 transition-colors text-left">
                    <ScoreRing score={pr.score} size={52} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white font-semibold font-mono">{pr.platform_code}</span>
                        <span className="text-gray-500 text-xs truncate">{pr.platform_url}</span>
                        {pr.has_api_key
                          ? <span className="text-[10px] text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded border border-green-500/20">API Key ✓</span>
                          : <span className="text-[10px] text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded border border-red-500/20">Sin API Key</span>}
                      </div>
                      <div className="flex gap-3 mt-2 flex-wrap">
                        {Object.entries(pr.checks).map(([key, check]) => (
                          <span key={key} className="flex items-center gap-1">
                            <StatusDot status={check.status} />
                            <span className="text-gray-500 text-[10px] capitalize">{key.replace(/_/g,' ')}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {pr.issue_summary.critical > 0 && (
                        <span className="text-xs text-red-400 bg-red-500/10 px-2 py-0.5 rounded-full border border-red-500/20">
                          {pr.issue_summary.critical} críticos
                        </span>
                      )}
                      {expandedPlatform === i
                        ? <ChevronUp className="w-4 h-4 text-gray-500" />
                        : <ChevronDown className="w-4 h-4 text-gray-500" />}
                    </div>
                  </button>

                  {expandedPlatform === i && (
                    <div className="border-t border-white/5 p-4 space-y-2">
                      {/* Check details */}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
                        {Object.entries(pr.checks).map(([key, check]) => {
                          const Icon = MODULE_ICONS[key] || Activity;
                          const cfg = check.status === 'ok' ? SEV_CFG.ok : check.status === 'warning' ? SEV_CFG.medium : SEV_CFG.critical;
                          return (
                            <div key={key} className={`p-2.5 rounded-xl border ${cfg.bg}`}>
                              <div className="flex items-center gap-2 mb-1">
                                <Icon className={`w-3.5 h-3.5 ${cfg.color}`} />
                                <span className={`text-xs font-semibold ${cfg.color} capitalize`}>{key.replace(/_/g,' ')}</span>
                              </div>
                              <p className="text-gray-400 text-[10px]">{check.detail}</p>
                            </div>
                          );
                        })}
                      </div>
                      {/* Issues list */}
                      {pr.issues.length > 0 && (
                        <div className="space-y-2">
                          {pr.issues.map((issue, j) => {
                            const cfg = SEV_CFG[issue.severity] || SEV_CFG.medium;
                            const Icon = cfg.icon;
                            const ModIcon = MODULE_ICONS[issue.module] || Activity;
                            return (
                              <div key={j} className={`p-3 rounded-xl border ${cfg.bg}`}>
                                <div className="flex items-start gap-2">
                                  <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${cfg.color}`} />
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
                                      <ModIcon className="w-3 h-3 text-gray-500" />
                                      <span className="text-gray-500 text-[10px] capitalize">{issue.module.replace(/_/g,' ')}</span>
                                    </div>
                                    <p className="text-gray-200 text-xs font-medium">{issue.problem}</p>
                                    <p className="text-gray-500 text-[10px] mt-1 italic">✦ Fix: {issue.fix}</p>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {pr.issues.length === 0 && (
                        <div className="text-center py-3">
                          <CheckCircle2 className="w-6 h-6 text-green-400 mx-auto mb-1" />
                          <p className="text-green-400 text-sm">Esta plataforma está completamente operativa</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {/* Global issues */}
              {report.global_issues?.length > 0 && (
                <div className="glass-panel overflow-hidden">
                  <button onClick={() => setExpandedIssues(!expandedIssues)}
                    className="w-full flex items-center justify-between p-4 hover:bg-white/3 transition-colors">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-red-400" />
                      <div>
                        <p className="text-white font-semibold text-sm">Problemas Globales del Sistema</p>
                        <p className="text-gray-500 text-xs">Afectan a todas las plataformas conectadas</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-red-400 bg-red-500/10 px-2 py-0.5 rounded-full border border-red-500/20">
                        {report.global_issues.length} problemas
                      </span>
                      {expandedIssues ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                    </div>
                  </button>
                  {expandedIssues && (
                    <div className="border-t border-white/5 p-4 space-y-3">
                      {report.global_issues.map((issue, i) => {
                        const cfg = SEV_CFG[issue.severity] || SEV_CFG.critical;
                        const Icon = cfg.icon;
                        return (
                          <div key={i} className={`p-3 rounded-xl border ${cfg.bg}`}>
                            <div className="flex items-start gap-2">
                              <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${cfg.color}`} />
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
                                  <span className="text-gray-500 text-[10px] capitalize">{issue.module?.replace(/_/g,' ')}</span>
                                </div>
                                <p className="text-gray-200 text-xs font-medium">{issue.problem}</p>
                                <p className="text-gray-500 text-[10px] mt-1 italic">✦ Fix: {issue.fix}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {report.platform_reports?.length === 0 && (
                <div className="glass-panel p-12 text-center">
                  <Link2 className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500">No hay plataformas vinculadas para diagnosticar.</p>
                  <p className="text-gray-600 text-sm mt-1">
                    Vincula una plataforma desde "Plataformas Conectadas" y ejecuta el diagnóstico.
  </p>
                </div>
              )}
            </div>
          )}

          {/* ── INSTRUCTIONS VIEW ── */}
          {activeView === 'instructions' && (
            <div className="space-y-4">
              {/* How-to */}
              <div className="glass-panel p-4 border border-blue-500/20 bg-blue-500/5">
                <h4 className="text-blue-400 font-semibold text-sm mb-2 flex items-center gap-2">
                  <Bot className="w-4 h-4" />
                  ¿Cómo usar estas instrucciones?
                </h4>
                <ol className="space-y-1.5 text-xs text-gray-400">
                  <li className="flex gap-2"><span className="text-blue-400 font-bold">1.</span>Haz clic en "Copiar Instrucciones" abajo</li>
                  <li className="flex gap-2"><span className="text-blue-400 font-bold">2.</span>Ve al chat de Replit Agent y pega el contenido</li>
                  <li className="flex gap-2"><span className="text-blue-400 font-bold">3.</span>El agente implementará todos los arreglos automáticamente</li>
                  <li className="flex gap-2"><span className="text-blue-400 font-bold">4.</span>Vuelve aquí y ejecuta el diagnóstico nuevamente para verificar</li>
                </ol>
              </div>

              {/* Copy button */}
              <div className="flex items-center gap-3">
                <button onClick={copyInstructions}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                    copied
                      ? 'bg-green-500 text-white'
                      : 'bg-red-500 hover:bg-red-600 text-white'
                  }`}>
                  {copied
                    ? <><CheckCircle2 className="w-4 h-4" />¡Copiado al portapapeles!</>
                    : <><ClipboardCopy className="w-4 h-4" />Copiar Instrucciones para Replit Agent</>}
                </button>
                <div className="text-gray-600 text-xs">
                  {report.llm_instructions?.length || 0} caracteres
                </div>
              </div>

              {/* Instructions preview */}
              {report.llm_instructions ? (
                <div className="glass-panel overflow-hidden">
                  <div className="flex items-center justify-between p-3 border-b border-white/5 bg-white/3">
                    <div className="flex items-center gap-2">
                      <Bot className="w-4 h-4 text-red-400" />
                      <span className="text-sm font-semibold text-gray-200">Instrucciones generadas por IA</span>
                    </div>
                    <button onClick={copyInstructions}
                      className="p-1.5 text-gray-500 hover:text-white hover:bg-white/10 rounded transition-colors">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="p-4 max-h-[60vh] overflow-y-auto">
                    <pre className="text-gray-300 text-xs leading-relaxed whitespace-pre-wrap font-mono">
                      {report.llm_instructions}
                    </pre>
                  </div>
                </div>
              ) : (
                <div className="glass-panel p-8 text-center">
                  <Bot className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500">Las instrucciones se generarán cuando ejecutes el diagnóstico.</p>
                </div>
              )}

              {/* Issue summary for reference */}
              {report.all_issues?.length > 0 && (
                <div className="glass-panel p-4">
                  <h4 className="text-sm font-semibold text-gray-200 mb-3">
                    📋 Resumen de problemas incluidos en las instrucciones
                  </h4>
                  <div className="space-y-1.5 max-h-60 overflow-y-auto">
                    {['critical','high','medium'].map(sev => {
                      const issues = report.all_issues.filter(i => i.severity === sev);
                      if (!issues.length) return null;
                      const cfg = SEV_CFG[sev];
                      const Icon = cfg.icon;
                      return issues.map((issue, j) => (
                        <div key={`${sev}-${j}`} className="flex items-start gap-2 text-xs">
                          <Icon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${cfg.color}`} />
                          <span className="text-gray-400">{issue.problem}</span>
                        </div>
                      ));
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Empty state */}
      {!report && !loading && (
        <div className="glass-panel p-12 text-center">
          <Bot className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-300 text-lg font-medium mb-2">Diagnóstico IA listo</p>
          <p className="text-gray-600 text-sm max-w-lg mx-auto mb-6">
            Haz clic en "Ejecutar Diagnóstico IA" para que el agente analice toda la infraestructura
            del Super Admin Connect y genere instrucciones precisas para Replit Agent.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-xl mx-auto text-xs text-gray-600">
            {[
              ['🔌 Conectividad', 'Verifica que todas las plataformas sean alcanzables'],
              ['🔑 API Auth', 'Valida que las API keys estén activas'],
              ['👥 Usuarios', 'Detecta usuarios sin propagar entre plataformas'],
              ['🤖 OkBot Agent', 'Verifica instalación como agente primario'],
            ].map(([title, desc]) => (
              <div key={title} className="bg-white/3 rounded-xl p-3">
                <p className="text-gray-400 font-medium mb-1">{title}</p>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
